"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const L = {
    fr: {
        title: "À partir de combien d'étapes changer d'outil",
        steps: "Étapes par scénario",
        runs: "Exécutions par mois",
        perOperation: "À l'opération",
        perTask: "À la tâche",
        perRun: "À l'exécution",
        perStep: "par étape",
        perExecution: "par exécution",
        cheapest: "le moins cher ici",
        crossLabel: "bascule",
        before: "En dessous de {n} étapes, payer à l'opération reste le moins cher.",
        after: "Au-delà de {n} étapes, payer à l'exécution devient le moins cher : le scénario compte pour un, quel que soit le nombre d'étapes.",
        axis: "étapes",
        note: "Tarifs indicatifs. Ce qui compte n'est pas le prix affiché mais le modèle : à l'opération et à la tâche, chaque étape se paie ; à l'exécution, le scénario entier compte pour un.",
    },
    en: {
        title: "How many steps before you change tool",
        steps: "Steps per scenario",
        runs: "Runs per month",
        perOperation: "Per operation",
        perTask: "Per task",
        perRun: "Per run",
        perStep: "per step",
        perExecution: "per run",
        cheapest: "cheapest here",
        crossLabel: "crossover",
        before: "Below {n} steps, paying per operation stays cheapest.",
        after: "Above {n} steps, paying per run becomes cheapest: the scenario counts as one, however many steps it has.",
        axis: "steps",
        note: "Indicative pricing. What matters is not the headline price but the model: per operation and per task, every step is paid for; per run, the whole scenario counts as one.",
    },
};

// Prix unitaires en euros, ordres de grandeur du marché.
const PRICE_PER_OPERATION = 0.0006;
const PRICE_PER_TASK = 0.008;
const PRICE_PER_RUN = 0.005;

const RUN_CHOICES = [500, 2000, 10000];
const MAX_STEPS = 30;

// Le seul point de bascule qui existe : à l'exécution, le prix ne dépend pas
// du nombre d'étapes, les deux autres si. Les droites se croisent donc une fois.
const CROSSOVER = PRICE_PER_RUN / PRICE_PER_OPERATION;

export default function PlatformCostCurve({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [steps, setSteps] = useState(8);
    const [runs, setRuns] = useState(2000);

    const costs = useMemo(
        () => ({
            operation: runs * steps * PRICE_PER_OPERATION,
            task: runs * steps * PRICE_PER_TASK,
            run: runs * PRICE_PER_RUN,
        }),
        [runs, steps],
    );

    const cheapest = useMemo(() => {
        const entries = [
            ["operation", costs.operation],
            ["task", costs.task],
            ["run", costs.run],
        ];
        return entries.sort((a, b) => a[1] - b[1])[0][0];
    }, [costs]);

    // Repère linéaire : les coûts sont proportionnels aux étapes, les tracés
    // sont donc des droites. Toute autre échelle donnerait une fausse courbure.
    const chart = useMemo(() => {
        const W = 620;
        const H = 190;
        const PAD_L = 8;
        const PAD_B = 26;
        const plotW = W - PAD_L;
        const plotH = H - PAD_B;

        const maxCost = runs * MAX_STEPS * PRICE_PER_OPERATION * 2.2;

        const x = (s) => PAD_L + ((s - 1) / (MAX_STEPS - 1)) * plotW;
        const y = (c) => plotH - Math.min(1, c / maxCost) * plotH;

        const line = (costAt) =>
            `M${x(1).toFixed(1)} ${y(costAt(1)).toFixed(1)} L${x(MAX_STEPS).toFixed(1)} ${y(costAt(MAX_STEPS)).toFixed(1)}`;

        return {
            W,
            H,
            plotH,
            x,
            y,
            operation: line((s) => runs * s * PRICE_PER_OPERATION),
            task: line((s) => runs * s * PRICE_PER_TASK),
            run: line(() => runs * PRICE_PER_RUN),
            crossX: x(CROSSOVER),
            crossY: y(runs * PRICE_PER_RUN),
            markerX: x(steps),
            ticks: [1, 10, 20, 30],
        };
    }, [runs, steps]);

    const money = (v) =>
        `${v < 10 ? v.toFixed(2).replace(".", lang === "fr" ? "," : ".") : Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;

    const big = (v) => v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

    const row = (key, label, unit, value, color) => (
        <div
            className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${
                cheapest === key ? "bg-green-500/10" : ""
            }`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <span className={`w-4 h-0.5 shrink-0 ${color}`} />
                <span className="text-sm text-gray-700 dark:text-lightBlue truncate">
                    {label}
                    <span className="text-xs opacity-60"> · {unit}</span>
                </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {cheapest === key && (
                    <span className="text-[10px] uppercase tracking-wide font-bold text-green-700 dark:text-green-400">
                        {t.cheapest}
                    </span>
                )}
                <span
                    className={`text-sm font-bold tabular-nums ${
                        cheapest === key
                            ? "text-green-700 dark:text-green-400"
                            : "dark:text-white"
                    }`}
                >
                    {money(value)}
                </span>
            </div>
        </div>
    );

    return (
        <div className="my-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6 text-base leading-normal">
            <div className="flex items-center gap-2 mb-5">
                <SolarIcon name="graph-up-linear" className="w-5 h-5 text-primary" />
                <div className="font-bold dark:text-white text-base leading-snug">
                    {t.title}
                </div>
            </div>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-sm text-gray-600 dark:text-lightBlue">
                    {t.runs}
                </span>
                {RUN_CHOICES.map((r) => (
                    <button
                        key={r}
                        type="button"
                        onClick={() => setRuns(r)}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors tabular-nums ${
                            runs === r
                                ? "border-primary text-primary bg-primary/10"
                                : "border-gray-200 dark:border-white/10 text-gray-600 dark:text-lightBlue"
                        }`}
                    >
                        {big(r)}
                    </button>
                ))}
            </div>

            <svg
                viewBox={`0 0 ${chart.W} ${chart.H}`}
                style={{ width: "100%", height: "auto", display: "block" }}
                className="text-gray-400 dark:text-grayDark-500"
                role="img"
                aria-label={t.title}
            >
                {/* Axe des étapes */}
                <line
                    x1="0"
                    y1={chart.plotH}
                    x2={chart.W}
                    y2={chart.plotH}
                    stroke="currentColor"
                    strokeOpacity="0.3"
                />
                {chart.ticks.map((s) => (
                    <g key={s}>
                        <line
                            x1={chart.x(s)}
                            y1={chart.plotH}
                            x2={chart.x(s)}
                            y2={chart.plotH + 4}
                            stroke="currentColor"
                            strokeOpacity="0.3"
                        />
                        <text
                            x={chart.x(s)}
                            y={chart.H - 6}
                            textAnchor="middle"
                            fontSize="12"
                            fontFamily="system-ui, sans-serif"
                            fill="currentColor"
                        >
                            {s === 30 ? `${s} ${t.axis}` : s}
                        </text>
                    </g>
                ))}

                {/* Position choisie */}
                <line
                    x1={chart.markerX}
                    y1="0"
                    x2={chart.markerX}
                    y2={chart.plotH}
                    stroke="currentColor"
                    strokeOpacity="0.45"
                    strokeWidth="1.5"
                    strokeDasharray="4 4"
                />

                <path d={chart.task} fill="none" stroke="#E8511D" strokeWidth="2.5" />
                <path d={chart.operation} fill="none" stroke="#0EA5E9" strokeWidth="2.5" />
                <path d={chart.run} fill="none" stroke="#16A34A" strokeWidth="2.5" />

                {/* Le croisement, seul point remarquable du graphique */}
                <circle
                    cx={chart.crossX}
                    cy={chart.crossY}
                    r="6"
                    fill="none"
                    stroke="#16A34A"
                    strokeWidth="2.5"
                />
                <text
                    x={chart.crossX}
                    y={chart.crossY - 14}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="600"
                    fontFamily="system-ui, sans-serif"
                    fill="#16A34A"
                >
                    {t.crossLabel} · {Math.round(CROSSOVER)}
                </text>
            </svg>

            <div className="mt-4">
                <div className="flex justify-between items-baseline mb-1.5">
                    <label className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                        {t.steps}
                    </label>
                    <span className="text-sm font-bold tabular-nums dark:text-white">
                        {steps}
                    </span>
                </div>
                <input
                    type="range"
                    min={1}
                    max={MAX_STEPS}
                    value={steps}
                    onChange={(e) => setSteps(Number(e.target.value))}
                    className="w-full accent-primary cursor-pointer"
                />
            </div>

            <div className="mt-4 space-y-1">
                {row("operation", t.perOperation, t.perStep, costs.operation, "bg-[#0EA5E9]")}
                {row("task", t.perTask, t.perStep, costs.task, "bg-[#E8511D]")}
                {row("run", t.perRun, t.perExecution, costs.run, "bg-[#16A34A]")}
            </div>

            <div
                className={`mt-4 text-sm font-semibold mb-0 ${
                    steps >= CROSSOVER
                        ? "text-green-700 dark:text-green-400"
                        : "text-gray-700 dark:text-lightBlue"
                }`}
            >
                {(steps >= CROSSOVER ? t.after : t.before).replace(
                    "{n}",
                    String(Math.round(CROSSOVER)),
                )}
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">
                {t.note}
            </p>
        </div>
    );
}
